import React, { useEffect, useState } from 'react';
import { Btn, DangerBtn, Loading, Msg, Section, panelPost, useAdminGet } from '../../kit';
import { LEADS, ScoringSettings } from './shared';

/**
 * Prospects → Scoring: the weights and caps of each score component, the
 * hot / warm bands, the thresholds and the nudge copy (app_settings
 * 'prospect_scoring', GET leads/prospect_settings, POST
 * leads/prospect_settings_set). The server validates and refuses a bad value.
 */

const numCls = 'h-8 w-20 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm';
const txtCls = 'h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-full';

const THRESHOLD_LABELS: Record<string, string> = {
  active_percentile: 'Trial-lapsed "active" from this activity percentile (win-back rule)',
  active_min_days: '"Active" = at least this many active days in the window …',
  active_min_searches: '… or at least this many searches in the window',
  intent_recent_days: 'Abandoned checkout / Concierge / IP Intel count when this recent (days)',
};

const ScoringEditor: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  const q = useAdminGet(LEADS, 'prospect_settings');
  const [s, setS] = useState<ScoringSettings | null>(null);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (q.data) setS(JSON.parse(JSON.stringify(q.data))); }, [q.data]);

  if (q.loading || !s) return q.error ? <Msg kind="error">{q.error}</Msg> : <Loading />;

  const save = async (reset = false) => {
    setBusy(true);
    const body = reset ? { reset: true } : {
      settings: { window_days: Number(s.window_days), weights: s.weights, bands: s.bands,
        thresholds: s.thresholds, nudges: s.nudges },
    };
    const { data, error } = await panelPost(LEADS, 'prospect_settings_set', body);
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setS(JSON.parse(JSON.stringify(data.settings)));
    setMsg({ kind: 'ok', text: reset ? 'Back to the defaults.' : 'Saved. The ranked list uses it from now on.' });
    onSaved();
  };
  const setW = (k: string, f: 'weight' | 'cap', v: string) =>
    setS({ ...s, weights: { ...s.weights, [k]: { ...s.weights[k], [f]: v === '' ? ('' as any) : Number(v) } } });
  const setN = (k: string, f: 'label' | 'why' | 'template_key', v: string) =>
    setS({ ...s, nudges: { ...s.nudges, [k]: { ...s.nudges[k], [f]: v } } });
  const totalWeight = Object.values(s.weights).reduce((a, w) => a + (Number(w.weight) || 0), 0);

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="Score components"
        right={<span className="text-xs text-grayscale-1100">weights add up to {totalWeight} (the score is capped at 100)</span>}>
        <p className="text-xs text-grayscale-1100">
          Each component earns up to its weight: weight × log(1 + value) / log(1 + cap). A value at the cap earns
          the full weight; one event already earns a good part of it. Profile % is linear.
        </p>
        <table className="text-sm">
          <thead><tr className="text-left text-xs text-grayscale-1100">
            <th className="py-1 pr-3">Component</th><th className="pr-3">Weight (0-100)</th><th className="pr-3">Cap</th><th>What counts</th>
          </tr></thead>
          <tbody>
            {s.components.map(c => (
              <tr key={c.key} className="border-t border-grayscale-200">
                <td className="py-1 pr-3">{c.label}</td>
                <td className="pr-3"><input type="number" className={numCls} min={0} max={100}
                  value={s.weights[c.key]?.weight as any} onChange={e => setW(c.key, 'weight', e.target.value)} /></td>
                <td className="pr-3"><input type="number" className={numCls} min={1}
                  value={s.weights[c.key]?.cap as any} onChange={e => setW(c.key, 'cap', e.target.value)} /></td>
                <td className="text-xs text-grayscale-1100">{c.help} (default {c.default_weight} / cap {c.default_cap})</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="Bands, window and thresholds">
        <div className="flex gap-4 flex-wrap text-sm items-center">
          <label className="flex items-center gap-2">Hot from
            <input type="number" className={numCls} value={s.bands.hot}
              onChange={e => setS({ ...s, bands: { ...s.bands, hot: Number(e.target.value) } })} /></label>
          <label className="flex items-center gap-2">Warm from
            <input type="number" className={numCls} value={s.bands.warm}
              onChange={e => setS({ ...s, bands: { ...s.bands, warm: Number(e.target.value) } })} /></label>
          <label className="flex items-center gap-2">Default window (days)
            <input type="number" className={numCls} value={s.window_days}
              onChange={e => setS({ ...s, window_days: Number(e.target.value) })} /></label>
        </div>
        <div className="flex flex-col gap-1 text-sm">
          {Object.keys(THRESHOLD_LABELS).map(k => (
            <label key={k} className="flex items-center gap-2">
              <input type="number" className={numCls} value={s.thresholds[k] as any}
                onChange={e => setS({ ...s, thresholds: { ...s.thresholds, [k]: Number(e.target.value) } })} />
              <span>{THRESHOLD_LABELS[k]}</span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Suggested nudges (first matching rule wins, in this order)">
        <div className="flex flex-col gap-2">
          {s.nudge_order.map((n, i) => (
            <div key={n.key} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center border-t border-grayscale-200 pt-2">
              <span className="md:col-span-1 text-xs text-grayscale-1100">{i + 1}. {n.key}</span>
              <input className={`${txtCls} md:col-span-3`} value={s.nudges[n.key]?.label || ''}
                onChange={e => setN(n.key, 'label', e.target.value)} placeholder="Label" />
              <input className={`${txtCls} md:col-span-5`} value={s.nudges[n.key]?.why || ''}
                onChange={e => setN(n.key, 'why', e.target.value)} placeholder="Why (shown with the suggestion)" />
              <input className={`${txtCls} md:col-span-3`} value={s.nudges[n.key]?.template_key || ''}
                onChange={e => setN(n.key, 'template_key', e.target.value)} placeholder="Template key (optional)" />
            </div>
          ))}
        </div>
        <p className="text-xs text-grayscale-1100">
          Rules: 1 abandoned checkout · 2 trial-lapsed and activity percentile at or above the threshold ·
          3 Explorer with trial intent or paywall hits who never had a trial · 4 blocked from contacts or asked
          for more · 5 IP Intel interest · 6 active · 7 everyone else. The template key is preselected in
          Campaigns when you follow up.
        </p>
      </Section>

      <div className="flex gap-2">
        <Btn kind="primary" disabled={busy} onClick={() => save(false)}>{busy ? 'Saving…' : 'Save scoring'}</Btn>
        <DangerBtn onConfirm={() => save(true)}>Reset to defaults</DangerBtn>
      </div>
    </div>
  );
};

export default ScoringEditor;
